import { useEffect, useRef } from 'react';
import { ChoiceRow } from '@/components/molecules';
import { dwrClassesById } from '@/data/classes';
import type { StandardRace } from '@/schemas/race';
import { computeArchetypeAutoSelect } from '../services/archetypeAutoSelect';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';

const RACE_LABELS: Record<StandardRace, string> = {
  umano: 'Umano',
  nano: 'Nano',
  elfo: 'Elfo',
  halfling: 'Halfling',
  mezzorco: "Mezz'orco",
};

/**
 * Step 3 — Selettore archetipo.
 *
 * Re-design Fase 2 PR 4: lista <ChoiceRow> filtrata sulla classe scelta.
 * L'archetipo associato alla razza scelta è marcato come "tipico della tua
 * razza" e viene **auto-preselezionato** se l'utente entra in sezione con
 * archetypeId vuoto (decisione G1 del WIP PR 4). La scelta resta libera:
 * razza e archetipo sono scelte indipendenti (memoria progetto).
 */
export function SectionArchetype() {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();

  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;
  const playerRaceId = draft.race?.kind === 'preset' ? draft.race.id : undefined;
  const suggestedArchetype =
    classDef && playerRaceId
      ? classDef.archetypes.find((a) => a.associatedRace === playerRaceId)
      : undefined;
  const suggestedId = suggestedArchetype?.id;

  // **G1 + FI-83 (2026-05-24)**: auto-select dell'archetipo tipico della
  // razza, con 2 trigger:
  //  1. Entry senza archetypeId → propone il suggested della razza corrente
  //     (comportamento storico G1).
  //  2. Cambio razza con archetypeId che era il suggested della razza
  //     vecchia → bump al suggested della razza nuova (FI-83). Le scelte
  //     manuali divergenti dal default sono preservate.
  //
  // Tracciamento `prevRaceId` via `useRef` per rilevare la transizione
  // razza → razza tra render. La logica vera vive nella pure helper
  // `computeArchetypeAutoSelect` (testabile unit, vedi sibling test file).
  const prevRaceIdRef = useRef<StandardRace | undefined>(playerRaceId);
  useEffect(() => {
    const prevRaceId = prevRaceIdRef.current;
    prevRaceIdRef.current = playerRaceId;
    if (!classDef) return;
    const nextArchetypeId = computeArchetypeAutoSelect(
      classDef,
      prevRaceId,
      playerRaceId,
      draft.archetypeId,
    );
    if (nextArchetypeId !== undefined && nextArchetypeId !== draft.archetypeId) {
      dispatch({ type: 'SET_ARCHETYPE', archetypeId: nextArchetypeId });
    }
  }, [playerRaceId, draft.archetypeId, classDef, dispatch]);

  if (!classDef) {
    return (
      <p className="text-ink-soft italic">
        Seleziona prima una classe nello step precedente per vedere gli archetipi disponibili.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-[0.4rem]">
      {classDef.archetypes.map((arch) => {
        const isSelected = draft.archetypeId === arch.id;
        const isSuggested = suggestedId === arch.id;
        const associatedRaceLabel = arch.associatedRace
          ? RACE_LABELS[arch.associatedRace]
          : undefined;

        return (
          <ChoiceRow
            key={arch.id}
            title={
              <span className="flex flex-wrap items-baseline gap-2">
                <span>{arch.name}</span>
                {isSuggested ? (
                  <span className="bg-paper-deep border-ink-soft font-display text-ink-soft inline-block border px-[0.5em] py-[0.1em] text-[0.62rem] tracking-[0.1em] uppercase">
                    Tipico della tua razza
                  </span>
                ) : null}
              </span>
            }
            description={arch.description}
            selected={isSelected}
            onClick={() => dispatch({ type: 'SET_ARCHETYPE', archetypeId: arch.id })}
          >
            {associatedRaceLabel ? (
              <div className="text-ink-faint mt-[0.3rem] text-[0.8rem]">
                Suggerito per la razza {associatedRaceLabel}.
              </div>
            ) : null}
          </ChoiceRow>
        );
      })}
    </div>
  );
}
